import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { auth, db } from '../../firebase';
import axios from 'axios';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const QuickQuoteModal = ({ open, onClose, conversation, companyId, onQuoteCreated }) => {
  const [mode, setMode] = useState('select'); // 'select' | 'quick' | 'measured'
  const [lineItems, setLineItems] = useState([{ description: '', price: '' }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Company tax settings — loaded once per open
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate]       = useState(0);

  useEffect(() => {
    if (open) {
      setMode('select');
      setLineItems([{ description: '', price: '' }]);
      setNotes('');
      setSaving(false);
      setError('');
      // Load billing settings every time the modal opens
      const loadTaxSettings = async () => {
        try {
          const currentUser = auth.currentUser;
          if (!currentUser) return;
          const token = await currentUser.getIdToken(true);
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          const res = await fetch(`${backendUrl}/api/invoices/settings/deposit`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;
          const data = await res.json();
          setTaxEnabled(!!data.taxEnabled);
          setTaxRate(typeof data.taxRate === 'number' ? data.taxRate : 0);
        } catch {
          // Non-blocking — fall back to no tax
        }
      };
      loadTaxSettings();
    }
  }, [open]);

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const value = parseFloat((item.price || '').toString().replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(value)) return sum;
      return sum + (value > 0 ? value : 0);
    }, 0);
  }, [lineItems]);

  const taxAmount  = (taxEnabled && taxRate > 0)
    ? parseFloat((subtotal * taxRate / 100).toFixed(2))
    : 0;
  const grandTotal = subtotal + taxAmount;

  const handleChangeItem = (index, field, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const ensureAtLeastOneRow = (items) => {
    return items.length === 0 ? [{ description: '', price: '' }] : items;
  };

  const handleRemoveItem = (index) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return ensureAtLeastOneRow(next);
    });
  };

  const handleKeyDownDescription = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const priceInput = document.querySelector(`#qq-price-${index}`);
      priceInput?.focus();
    }
  };

  const handleKeyDownPrice = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setLineItems((prev) => {
        const next = [...prev, { description: '', price: '' }];
        return next;
      });
      // Focus will move on next render
      setTimeout(() => {
        const nextIndex = index + 1;
        const descInput = document.querySelector(`#qq-desc-${nextIndex}`);
        descInput?.focus();
      }, 0);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!conversation?.id) {
      setError('No conversation selected');
      return;
    }
    const cleaned = lineItems
      .map((item) => {
        const price = parseFloat((item.price || '').toString().replace(/[^0-9.]/g, '')) || 0;
        const description = (item.description || '').trim();
        return { description, price };
      })
      .filter((item) => item.price > 0 || item.description);

    if (cleaned.length === 0) {
      setError('Add at least one priced line item');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('You must be logged in to create a quote');
      return;
    }

    try {
      setSaving(true);
      const token = await currentUser.getIdToken(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inbox-quotes/quick`,
        {
          conversationId: conversation.id,
          lineItems: cleaned,
          notes: notes.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { quoteId, total: quoteTotal, publicUrl } = res.data || {};
      if (!quoteId) {
        setError('Unexpected response when saving quote');
        return;
      }

      // Write system message: quote_created
      // Store the full publicUrl so MessageBubble can use it directly
      try {
        if (db && companyId && conversation?.id) {
          await addDoc(
            collection(
              db,
              'companies',
              companyId,
              'conversations',
              conversation.id,
              'messages'
            ),
            {
              type: 'internal',
              kind: 'quote_created',
              quoteId,
              publicUrl,  // Store full URL for future reference
              total: quoteTotal,
              from: 'system',
              timestamp: serverTimestamp(),
            }
          );
        }
      } catch (e) {
        console.error('Failed to write quote_created system message', e);
      }

      if (onQuoteCreated) {
        onQuoteCreated({ quoteId, total: quoteTotal, publicUrl });
      }

      onClose();
    } catch (err) {
      console.error('Failed to save quick quote', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const renderModeSelection = () => (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-gray-900">Create a quote</h2>
        <p className="text-xs text-gray-500">Choose how you want to price this.</p>
      </div>
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setMode('quick')}
          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-left hover:bg-gray-50 transition flex flex-col"
        >
          <span className="text-sm font-medium text-gray-900">Quick Quote</span>
          <span className="text-xs text-gray-500">Fast, manual pricing</span>
        </button>
        <button
          type="button"
          onClick={() => setMode('measured')}
          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-left hover:bg-gray-50 transition flex flex-col"
        >
          <span className="text-sm font-medium text-gray-900">Measured Quote</span>
          <span className="text-xs text-gray-500">Use map & measurements</span>
        </button>
      </div>
    </div>
  );

  const renderQuickQuote = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Create Quick Quote</h2>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">Line Items</p>
        <div className="space-y-2">
          {lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-[minmax(0,1fr)_100px_auto] gap-2 items-center">
              <Input
                id={`qq-desc-${index}`}
                placeholder="Service / Description"
                value={item.description}
                onChange={(e) => handleChangeItem(index, 'description', e.target.value)}
                onKeyDown={(e) => handleKeyDownDescription(e, index)}
              />
              <Input
                id={`qq-price-${index}`}
                placeholder="$0.00"
                value={item.price}
                onChange={(e) => handleChangeItem(index, 'price', e.target.value)}
                onKeyDown={(e) => handleKeyDownPrice(e, index)}
              />
              {lineItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="text-xs text-gray-400 hover:text-gray-600 px-1"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setLineItems((prev) => [...prev, { description: '', price: '' }])}
            className="text-xs text-indigo-600 hover:underline mt-1"
          >
            + Add line item
          </button>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-700" htmlFor="qq-notes">
          Notes (optional)
        </label>
        <Textarea
          id="qq-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional note for the customer…"
          rows={3}
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="pt-2 space-y-1.5">
        {taxAmount > 0 ? (
          <div className="space-y-1 text-sm text-gray-700 border-t pt-2" data-testid="qq-tax-breakdown">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-medium" data-testid="qq-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax ({taxRate}%)</span>
              <span className="font-medium" data-testid="qq-tax-amount">${taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold border-t pt-1" data-testid="qq-grand-total-row">
              <span>Total</span>
              <span data-testid="qq-grand-total">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between text-sm text-gray-700 border-t pt-2">
            <span>Total</span>
            <span className="font-semibold" data-testid="qq-grand-total">${grandTotal.toFixed(2)}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Quote'}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderBody = () => {
    if (mode === 'select') return renderModeSelection();
    if (mode === 'quick') return renderQuickQuote();
    // Measured quote path: navigate to calculator and close.
    if (mode === 'measured') {
      window.location.href = '/calculator';
      onClose();
      return null;
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Create a quote</DialogTitle>
          <DialogDescription className="text-xs">
            Choose how you want to price this conversation.
          </DialogDescription>
        </DialogHeader>
        {renderBody()}
        {mode === 'select' && (
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickQuoteModal;
